import React, {useContext, useState, useEffect} from 'react';
import {Card, Col, Container, Row, Spinner, Button} from "react-bootstrap";
import {ControlUnitService} from "./ControlUnitService";
import EditIcon from '@mui/icons-material/Edit';
import NoteIcon from '@mui/icons-material/Note';
import {store} from "../../Store/store";
import BeatLoader from "react-spinners/BeatLoader";
import PowerIcon from '@mui/icons-material/Power';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import Battery0BarIcon from '@mui/icons-material/Battery0Bar';
import {IoCheckmark} from "react-icons/io5";
import {MdReportGmailerrorred} from "react-icons/md";
import BatteryStdIcon from '@mui/icons-material/BatteryStd';
import {ControlUnitStateService} from "./controlUnitStateService";
import {getCurrentAuthToken} from "../../General/SharedMethods";
import {EventSourcePolyfill, NativeEventSource} from "event-source-polyfill";
import Alert from '@mui/material/Alert';
import FmdBadIcon from '@mui/icons-material/FmdBad';
import DescriptionIcon from '@mui/icons-material/Description';
import moment from "moment";
import SettingsInputAntennaIcon from '@mui/icons-material/SettingsInputAntenna';
import EthernetIcon from '@mui/icons-material/SettingsEthernet';
import WifiIcon from '@mui/icons-material/Wifi';
import {CellWifi} from "@mui/icons-material";

const MAX_NOTE_LENGTH = 1500;

const OverviewPage = ({controlUnitSerialNumber, hardwareVersion, controlUnitId, connectionState}) => {
    const controlUnitStateService = new ControlUnitStateService(process.env.REACT_APP_SUPPORT_DASHBOARD_CONTROL_UNITS_URL);
    const controlUnitService = new ControlUnitService(process.env.REACT_APP_SUPPORT_DASHBOARD_CONTROL_UNITS_URL);
    const globalState = useContext(store);
    const [showSpinner, setShowSpinner] = useState(true);
    const [acStatus, setAcStatus] = useState(null);
    const [batteryStatus, setBatteryStatus] = useState(null);
    const [ioModuleStatus, setIoModuleStatus] = useState(null);
    const [cuConfigFileSystemTime, setCuConfigFileSystemTime] = useState(null);
    const [cuConfigFileBrowserTime, setCuConfigFileBrowserTime] = useState(null);
    const [isCuStateRequestInProgress, setIsCuStateRequestInProgress] = useState(false);

    // Admin Notes state
    const [noteText, setNoteText] = useState('');
    const [editedNoteText, setEditedNoteText] = useState('');
    const [noteLastUpdated, setNoteLastUpdated] = useState(null);
    const [noteLastUpdatedBy, setNoteLastUpdatedBy] = useState(null);
    const [isNoteEditMode, setIsNoteEditMode] = useState(false);
    const [isNoteSaving, setIsNoteSaving] = useState(false);
    const [isNoteLoading, setIsNoteLoading] = useState(true);
    const statusOptions = [
        {value: 'AC', label: 'AC'},
        {value: 'BATTERY', label: 'Battery'},
        {value: 'UNPLUGGED', label: 'Unplugged'},
        {value: 'OK', label: 'Ok'},
        {value: 'LOW', label: 'Low'},
        {value: 'NO_BATTERY', label: 'No Battery'},
        {value: 'CONNECTED', label: 'Connected'},
        {value: 'DISCONNECTED', label: 'Disconnected'},
        {value: 'UNKNOWN', label: 'Unknown'},
        {value: 'UPDATED', label: 'Up-to-date'}
    ];
    const communicationStatusOptions = [
        {value: 'UP', label: 'Connected'},
        {value: 'DOWN', label: 'Disconnected'},
        {value: 'UNKNOWN', label: 'Unknown'}
    ]
    const [errorMessage, setErrorMessage] = useState(null);
    const [ethernetStatus, setEthernetStatus] = useState(null);
    const [wifiStatus, setWifiStatus] = useState(null);
    const [cellularStatus, setCellularStatus] = useState(null);
    const [isEthernetActive, setIsEthernetActive] = useState(null);
    const [isWifiActive, setIsWifiActive] = useState(null);
    const [isCellularActive, setIsCellularActive] = useState(null);

    const getCuState = async () => {
        try {
            setIsCuStateRequestInProgress(true);
            const response = await controlUnitStateService.getControlUnitState(controlUnitId);
            setAcStatus(response.data.power);
            setBatteryStatus(response.data.batteryStatus);
            setIoModuleStatus(response.data.dioState);
            setCuConfigFileBrowserTime(response.data.configUpdatedAt);
            setCuConfigFileSystemTime(response.data.configUpdatedAtSiteTime);
            setEthernetStatus(response.data.communicationChannels.ethernet.status);
            setWifiStatus(response.data.communicationChannels.wifi.status);
            setCellularStatus(response.data.communicationChannels.cellular.status);
            setIsEthernetActive(response.data.communicationChannels.ethernet.active);
            setIsWifiActive(response.data.communicationChannels.wifi.active);
            setIsCellularActive(response.data.communicationChannels.cellular.active);
            setIsCuStateRequestInProgress(false);
        } catch (e) {
            setIsCuStateRequestInProgress(false);
            console.error(e);
        }

    }

    const getNote = async () => {
        try {
            setIsNoteLoading(true);
            const response = await controlUnitService.getNote(controlUnitId);
            if (response.data) {
                setNoteText(response.data.note || '');
                setNoteLastUpdated(response.data.updated);
                setNoteLastUpdatedBy(response.data.email);
            }
        } catch (e) {
            console.error('Error fetching note:', e);
        } finally {
            setIsNoteLoading(false);
        }
    };

    const handleNoteSave = async () => {
        try {
            setIsNoteSaving(true);
            await controlUnitService.updateNote(controlUnitId, editedNoteText);
            // Re-fetch the note to get updated timestamp and user info
            await getNote();
            setIsNoteEditMode(false);
        } catch (e) {
            console.error('Error saving note:', e);
        } finally {
            setIsNoteSaving(false);
        }
    };

    const handleNoteCancel = () => {
        setEditedNoteText(noteText);
        setIsNoteEditMode(false);
    };

    const handleNoteEdit = () => {
        setEditedNoteText(noteText);
        setIsNoteEditMode(true);
    };

    const isNoteSaveDisabled = editedNoteText.length > MAX_NOTE_LENGTH;

    const displayValue = (value) => {
        if (value) {
            return value;
        } else {
            return showSpinner ? <div className={'d-inline'}>
                <BeatLoader
                    loading={true}
                    size={2}
                    color={'grey'}
                    aria-label="Loading Spinner"
                    data-testid="loader"
                />
            </div> : 'N/A';
        }
    };

    const renderStatusIcon = (status) => {
        switch (status) {
            case 'AC':
            case 'OK':
            case 'CONNECTED':
            case  'UPDATED':
                return <IoCheckmark size={50} color={'green'}/>
            default:
                return <MdReportGmailerrorred size={50} color={'red'}/>

        }
    }

    const renderStatusLabel = (status) => {
        const label = statusOptions.find(obj => obj.value === status)?.label;
        return label ? label : 'N/A'
    }

    const renderUpdateTime = (time) => {
        return time ? moment(time).format('lll') : 'N/A';
    }

    const StatusIndicator = ({
                                 title,
                                 status,
                                 icon,
                                 lastUpdatedSystemTime,
                                 lastUpdatedBrowserTime,
                                 isConfigurationFile
                             }) => {
        return (
            <Card style={{height: '100%'}}>
                <Card.Body className='p-3'>
                    <Row className="no-gutters align-items-center justify-content-start">
                        <Col xs="auto" className="pr-2">
                            <span className="icon-placeholder">{icon}</span>
                        </Col>
                        <Col xs={8}>
                            <h6 className="mb-0">{title}</h6>
                        </Col>
                        {!isConfigurationFile ? <Col xs={12} className={"mt-2"}>
                                {renderStatusIcon(status)}
                            </Col> :
                            <Col xs={12} style={{textDecoration: 'underline'}} className={"mt-1 mb-1"}>Last Updated
                                At:</Col>}
                    </Row>
                    {isConfigurationFile ? null : <Row>
                        <Col className="mt-3">
                            <h6>{renderStatusLabel(status)}</h6>
                        </Col>
                    </Row>}
                    <Row>
                        <Col>
                            {isConfigurationFile &&
                                <div style={{fontSize: 14}}>{renderUpdateTime(lastUpdatedSystemTime)} (System
                                    Time)</div>}
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            {isConfigurationFile &&
                                <div style={{fontSize: 14}}>{renderUpdateTime(lastUpdatedBrowserTime)} (Browser
                                    Time)</div>}
                        </Col>
                    </Row>
                </Card.Body>
            </Card>
        );
    }

    const getCommunicationStatusLabel = (communicationStatus) => {
        const res = communicationStatusOptions.find(status => status.value === communicationStatus);
        if (res) {
            switch (res.label) {
                case 'Connected':
                    return <div className='text-info'>Connected</div>
                case 'Disconnected':
                    return <div className='text-danger'>Disconnected</div>
                case 'Unknown':
                    return <div>Unknown</div>
            }
        }
        return <div>Unknown</div>;
    }

    useEffect(() => {
        getCuState();
        getNote();
    }, [])

    useEffect(() => {
        const timer = setTimeout(() => {
            setShowSpinner(false);
        }, 5000);

        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        if (controlUnitId) {
            const authHeaderValue = getCurrentAuthToken();
            const eventSourceInitDict = {
                headers: {
                    'Authorization': authHeaderValue
                },
            };
            const EventSource = NativeEventSource && NativeEventSource.supportsHeaders ? NativeEventSource : EventSourcePolyfill;
            const eventSource = new EventSource(`${process.env.REACT_APP_SUPPORT_DASHBOARD_CONTROL_UNITS_URL}/api/control-units/${controlUnitId}/state-stream`, eventSourceInitDict);
            eventSource.onmessage = (event) => {
                setErrorMessage(null);
                const response = JSON.parse(event.data);
                setAcStatus(response.power);
                setBatteryStatus(response.batteryStatus);
                setIoModuleStatus(response.dioState);
                setCuConfigFileBrowserTime(response.configUpdatedAt);
                setCuConfigFileSystemTime(response.configUpdatedAtSiteTime);
            };

            eventSource.onerror = (e) => {
                e?.error?.toString().includes('Error: No activity within 45000 milliseconds. No response received. Reconnecting.') ?
                    setErrorMessage(null) : setErrorMessage('A connection error occurred, Please try again later.');
            }
            return () => {
                eventSource.close();
            };
        }
    }, [controlUnitId]);

    return (
        <>
            <Row style={{marginRight: '230px'}} className='mb-2 ml-1'>
                <Col md={{span: 9, offset: 2}}>
                    {errorMessage && (
                        <Alert severity="error">{errorMessage}</Alert>
                    )}
                    <Card bsPrefix='tabCard'>
                        <Card.Body style={{textAlign: 'left', paddingBottom: '10px'}}>
                            <div><strong>S/N: </strong>{displayValue(controlUnitSerialNumber)}</div>
                            <div><strong>Installed Package
                                Version: </strong> {displayValue(globalState.state.cuCurrentVersion)} (Installed
                                on: {displayValue(globalState.state.cuUpdateDate)}) | Newest Version
                                Available: {displayValue(globalState.state.cuNewestVersion)}</div>
                            <div><strong>Hardware Version: </strong> {displayValue(hardwareVersion)}</div>
                            {
                                connectionState === 'CONNECTED' ?
                                    null :
                                    <div className='mt-2'><FmdBadIcon fontSize={'small'} color={'warning'}
                                                                      className='mb-1'/>Note:
                                        while CU is disconnected, the last known states are displayed
                                    </div>
                            }
                        </Card.Body>
                        {isCuStateRequestInProgress ? <Spinner animation='border' size='sm' variant='primary'/> :
                            <Container className='pb-3 pr-3 pl-3 pt-0' fluid>
                                <Row>
                                    <Col md={3}>
                                        <StatusIndicator
                                            title="AC Power"
                                            status={acStatus}
                                            icon={<PowerIcon color='info'/>}
                                        />
                                    </Col>
                                    <Col md={3}>
                                        <StatusIndicator
                                            title="Battery Status"
                                            status={batteryStatus}
                                            icon={batteryStatus === 'OK' ? <BatteryStdIcon color='info'/> :
                                                <Battery0BarIcon color='info'/>}
                                        />
                                    </Col>
                                    <Col md={3}>
                                        <StatusIndicator
                                            title="I/O Module"
                                            status={ioModuleStatus}
                                            icon={<SwapHorizIcon color='info'/>}
                                        />
                                    </Col>
                                    <Col md={3}>
                                        <StatusIndicator
                                            title="CU Configuration File"
                                            status="UPDATED"
                                            isConfigurationFile={true}
                                            lastUpdatedSystemTime={cuConfigFileSystemTime}
                                            lastUpdatedBrowserTime={cuConfigFileBrowserTime}
                                            icon={<DescriptionIcon fontSize='small' color='info'/>}
                                        />
                                    </Col>
                                </Row>
                                <Row className='mt-3 ml-1'>
                                    <Card style={{height: '100%'}}>
                                        <Card.Body className='p-3'>
                                            <Container className="p-2">
                                                <Container className="p-0 mb-2">
                                                    <Row className="align-items-center p-2">
                                                        <Col>
                                                            <div className='mr-3' style={{display: 'inline-block'}}>
                                                                <SettingsInputAntennaIcon fontSize='small'
                                                                                          color='info'/>
                                                            </div>
                                                            <div style={{display: 'inline-block'}}>
                                                                <h6 className="mb-0">Communication Channel</h6>
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                </Container>
                                                <Container className="p-0 mb-2">
                                                    <Row className="align-items-center p-2">
                                                        <Col className='p-0' xs="auto" style={{width: '30px'}}>
                                                            <EthernetIcon fontSize='small' color='info'/>
                                                        </Col>
                                                        <Col className='p-0' style={{width: '300px'}}>
                                                            <span style={{fontWeight: '500'}}>ETH (Default)</span>
                                                        </Col>
                                                        <Col className='p-0' style={{width: '80px'}}>
                                                            {getCommunicationStatusLabel(ethernetStatus)}
                                                        </Col>
                                                        <Col className='p-0' xs="auto">
                                                            <div className="text-muted">|</div>
                                                        </Col>
                                                        <Col className='p-0' style={{width: '100px'}}>
                                                            <div
                                                                className={isEthernetActive ? "text-primary" : ''}>{isEthernetActive ? 'Active' : 'Inactive'}</div>
                                                        </Col>
                                                    </Row>
                                                </Container>
                                                <Container className="p-0 mb-2">
                                                    <Row className="align-items-center p-2">
                                                        <Col className='p-0' xs="auto" style={{width: '30px'}}>
                                                            <WifiIcon fontSize='small' color='info'/>
                                                        </Col>
                                                        <Col className='p-0' style={{width: '300px'}}>
                                                            <span style={{fontWeight: '500'}}>WiFi (2nd priority)</span>
                                                        </Col>
                                                        <Col className='p-0' style={{width: '80px'}}>
                                                            {getCommunicationStatusLabel(wifiStatus)}
                                                        </Col>
                                                        <Col className='p-0' xs="auto">
                                                            <div className="text-muted">|</div>
                                                        </Col>
                                                        <Col className='p-0' style={{width: '100px'}}>
                                                            <div
                                                                className={isWifiActive ? "text-primary" : ''}>{isWifiActive ? 'Active' : 'Inactive'}</div>
                                                        </Col>
                                                    </Row>
                                                </Container>
                                                <Container className="p-0">
                                                    <Row className="align-items-center p-2">
                                                        <Col className='p-0' xs="auto" style={{width: '30px'}}>
                                                            <CellWifi size={16} color="info"/>
                                                        </Col>
                                                        <Col className='p-0' style={{width: '300px'}}>
                                                            <span style={{fontWeight: '500'}}>Cell (3rd priority)</span>
                                                        </Col>
                                                        <Col className='p-0' style={{width: '80px'}}>
                                                            {getCommunicationStatusLabel(cellularStatus)}
                                                        </Col>
                                                        <Col className='p-0' xs="auto">
                                                            <div className="text-muted">|</div>
                                                        </Col>
                                                        <Col className='p-0' style={{width: '100px'}}>
                                                            <div
                                                                className={isCellularActive ? "text-primary" : ''}>{isCellularActive ? 'Active' : 'Inactive'}</div>
                                                        </Col>
                                                    </Row>
                                                </Container>
                                            </Container>
                                        </Card.Body>
                                    </Card>
                                </Row>
                                {/* Admin Notes Section */}
                                <Row className='mt-3'>
                                    <Col md={12}>
                                        <Card>
                                            <Card.Body className='p-3'>
                                            <Container className="p-2">
                                                <Container className="p-0 mb-2">
                                                    <Row className="align-items-center p-2">
                                                        <Col>
                                                            <div className='mr-3' style={{display: 'inline-block'}}>
                                                                <NoteIcon fontSize='small' color='info'/>
                                                            </div>
                                                            <div style={{display: 'inline-block'}}>
                                                                <h6 className="mb-0">Admin Notes</h6>
                                                            </div>
                                                        </Col>
                                                        {!isNoteEditMode && noteText && (
                                                            <Col xs="auto">
                                                                <Button
                                                                    variant="link"
                                                                    className="p-0"
                                                                    onClick={handleNoteEdit}
                                                                >
                                                                    <EditIcon fontSize='small' color='action'/>
                                                                </Button>
                                                            </Col>
                                                        )}
                                                    </Row>
                                                </Container>
                                                {isNoteLoading ? (
                                                    <div className="text-center p-3">
                                                        <Spinner animation='border' size='sm' variant='primary'/>
                                                    </div>
                                                ) : isNoteEditMode ? (
                                                    // Edit Mode
                                                    <Container className="p-0">
                                                        <textarea
                                                            value={editedNoteText}
                                                            onChange={(e) => setEditedNoteText(e.target.value)}
                                                            style={{
                                                                width: '100%',
                                                                minHeight: '150px',
                                                                maxHeight: '200px',
                                                                padding: '10px',
                                                                borderRadius: '4px',
                                                                border: '1px solid #ced4da',
                                                                resize: 'vertical',
                                                                fontFamily: 'inherit',
                                                                fontSize: '14px'
                                                            }}
                                                            placeholder="Enter admin notes here..."
                                                        />
                                                        <div className="d-flex justify-content-between align-items-center mt-2">
                                                            <span style={{
                                                                fontSize: '12px',
                                                                color: isNoteSaveDisabled ? 'red' : '#6c757d'
                                                            }}>
                                                                {editedNoteText.length}/{MAX_NOTE_LENGTH} characters
                                                            </span>
                                                            <div>
                                                                <Button
                                                                    variant="outline-secondary"
                                                                    size="sm"
                                                                    className="mr-2"
                                                                    onClick={handleNoteCancel}
                                                                    disabled={isNoteSaving}
                                                                >
                                                                    Cancel
                                                                </Button>
                                                                <Button
                                                                    variant="primary"
                                                                    size="sm"
                                                                    onClick={handleNoteSave}
                                                                    disabled={isNoteSaveDisabled || isNoteSaving}
                                                                >
                                                                    {isNoteSaving ? (
                                                                        <Spinner animation='border' size='sm'/>
                                                                    ) : 'Save'}
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </Container>
                                                ) : noteText ? (
                                                    // View Mode with note
                                                    <Container className="p-0">
                                                        <div style={{
                                                            maxHeight: '200px',
                                                            overflowY: 'auto',
                                                            padding: '10px',
                                                            backgroundColor: '#f8f9fa',
                                                            borderRadius: '4px',
                                                            whiteSpace: 'pre-wrap',
                                                            fontSize: '14px',
                                                            textAlign: 'left'
                                                        }}>
                                                            {noteText}
                                                        </div>
                                                        {(noteLastUpdated || noteLastUpdatedBy) && (
                                                            <div style={{
                                                                fontSize: '12px',
                                                                color: '#6c757d',
                                                                marginTop: '8px'
                                                            }}>
                                                                {noteLastUpdatedBy && <span>Last updated by: {noteLastUpdatedBy}</span>}
                                                                {noteLastUpdated && noteLastUpdatedBy && <span> | </span>}
                                                                {noteLastUpdated && <span>{moment(noteLastUpdated).format('lll')}</span>}
                                                            </div>
                                                        )}
                                                    </Container>
                                                ) : (
                                                    // Empty State
                                                    <Container className="p-0 text-center">
                                                        <div style={{
                                                            padding: '20px',
                                                            backgroundColor: '#f8f9fa',
                                                            borderRadius: '4px'
                                                        }}>
                                                            <p style={{
                                                                color: '#6c757d',
                                                                marginBottom: '15px',
                                                                fontSize: '14px'
                                                            }}>
                                                                No admin notes yet
                                                            </p>
                                                            <Button
                                                                variant="primary"
                                                                size="sm"
                                                                onClick={handleNoteEdit}
                                                            >
                                                                Add Note
                                                            </Button>
                                                        </div>
                                                    </Container>
                                                )}
                                            </Container>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                </Row>
                            </Container>}
                    </Card>
                </Col>
            </Row>
        </>
    );
};

export default OverviewPage;
